import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Info, Sparkles, Video, Check, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convertir URL de Google Drive a embebido
const driveEmbed = (url) => {
  if (!url) return null;
  // formatos comunes:
  // https://drive.google.com/file/d/FILE_ID/view
  // https://drive.google.com/open?id=FILE_ID
  const m1 = url.match(/\/file\/d\/([^/]+)/);
  const m2 = url.match(/[?&]id=([^&]+)/);
  const id = (m1 && m1[1]) || (m2 && m2[1]);
  if (id) return `https://drive.google.com/file/d/${id}/preview`;
  return url;
};

const SECTION_LABELS = {
  activacion: { label: 'Activación', color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  principal: { label: 'Principal', color: 'bg-pink-500/15 text-pink-300 border-pink-500/30' },
  complementario: { label: 'Complementario', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  secundario: { label: 'Complementario', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' }
};

const WorkoutDayDetail = () => {
  const { dayNumber } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('session_token') || localStorage.getItem('token');

  const [loading, setLoading] = useState(true);
  const [routine, setRoutine] = useState(null);
  const [openTechnique, setOpenTechnique] = useState(null);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    const fetchRoutine = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/workout-routine`, { headers: { Authorization: `Bearer ${token}` } });
        setRoutine(res.data.routine);
      } catch (err) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchRoutine();
  }, [token]);

  const isCompletedThisWeek = useMemo(() => {
    if (!routine) return false;
    const created = new Date(routine.created_at);
    const weeksSinceStart = Math.floor((new Date() - created) / (7 * 24 * 60 * 60 * 1000));
    const currentWeek = weeksSinceStart + 1;
    return (routine.days_completed || []).some(c => c.day_number === Number(dayNumber) && c.week_number === currentWeek);
  }, [routine, dayNumber]);

  const handleToggleComplete = async () => {
    setMarking(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/workout-routine/day/${dayNumber}/toggle-complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      // Refrescar la rutina
      const r = await axios.get(`${BACKEND_URL}/api/workout-routine`, { headers: { Authorization: `Bearer ${token}` } });
      setRoutine(r.data.routine);
      if (res.data.action === 'completed') {
        toast.success('¡Día completado! 💪');
      } else {
        toast.info('Día desmarcado');
      }
    } catch (err) {
      toast.error('No se pudo actualizar');
    } finally {
      setMarking(false);
    }
  };

  const day = useMemo(() => {
    if (!routine) return null;
    return (routine.days || []).find(d => String(d.day_number) === String(dayNumber));
  }, [routine, dayNumber]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
      </div>
    );
  }

  if (!day) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-zinc-400 mb-4">No encontramos este día en tu rutina.</p>
          <Button onClick={() => navigate('/training-plan')} className="bg-pink-600 hover:bg-pink-500 rounded-full">Volver a mi plan</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/training-plan')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-6"
          data-testid="back-to-plan-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Mi plan
        </button>

        {/* Header día */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-14 h-14 rounded-2xl bg-pink-500 flex items-center justify-center font-barlow font-black text-2xl text-white">
              {day.day_number}
            </div>
            <div>
              <p className="text-pink-400 text-xs font-bold uppercase tracking-widest">Día {day.day_number}</p>
              <h1 className="font-barlow text-2xl md:text-3xl font-black text-white uppercase leading-tight">
                {day.focus}
              </h1>
            </div>
          </div>
        </div>

        {/* Ejercicios */}
        <div className="space-y-5">
          {(day.exercises || []).map((ex, idx) => {
            const section = SECTION_LABELS[ex.section] || SECTION_LABELS.principal;
            const videoUrl = driveEmbed(ex.video_url);
            return (
              <div key={`${ex.exercise_id}-${idx}`} className="bg-zinc-900/60 border border-zinc-800 rounded-3xl overflow-hidden" data-testid={`day-exercise-${idx}`}>                {/* Video */}
                <div className="relative aspect-video bg-zinc-950 border-b border-zinc-800">
                  {videoUrl ? (
                    <iframe
                      src={videoUrl}
                      className="w-full h-full"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                      title={ex.name}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-zinc-600">
                      <Video className="w-12 h-12" />
                    </div>
                  )}
                </div>

                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold text-lg leading-tight">{ex.name}</h3>
                      <p className="text-zinc-500 text-xs mt-0.5 capitalize">{(ex.muscle_group || '').replace('_', ' ')}</p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${section.color}`}>{section.label}</span>
                  </div>

                  {/* Series profesionales (estilo coach 1-1) */}
                  {Array.isArray(ex.series) && ex.series.length > 0 && (
                    <ul className="space-y-2 mb-4" data-testid={`series-list-${idx}`}>
                      {ex.series.map((s, sIdx) => (
                        <li key={sIdx} className="flex gap-3 items-start bg-zinc-800/40 rounded-xl p-3 border border-zinc-800/60">
                          <span className="flex-shrink-0 w-6 h-6 rounded-md bg-pink-500/15 text-pink-300 text-xs font-bold flex items-center justify-center mt-0.5">
                            {sIdx + 1}
                          </span>
                          <span className="text-zinc-100 text-sm leading-relaxed">{s}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Técnica */}
                  {ex.technique && (
                    <button
                      onClick={() => setOpenTechnique(openTechnique === idx ? null : idx)}
                      className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-xl hover:bg-amber-500/15 transition-colors"
                      data-testid={`technique-toggle-${idx}`}
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        <span className="text-amber-300 text-sm font-bold uppercase tracking-wider">{ex.technique.name}</span>
                      </div>
                      <Info className="w-4 h-4 text-amber-400" />
                    </button>
                  )}
                  {ex.technique && openTechnique === idx && (
                    <div className="mt-2 bg-zinc-800/60 rounded-xl p-4">
                      {ex.technique.description && <p className="text-zinc-300 text-sm mb-2">{ex.technique.description}</p>}
                      {ex.technique.how_to_apply && (
                        <>
                          <p className="text-[11px] text-pink-400 font-bold uppercase tracking-wider mb-1">Cómo aplicar</p>
                          <p className="text-zinc-300 text-sm">{ex.technique.how_to_apply}</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Botón marcar día completado */}
        <div className="mt-8 mb-12">
          <button
            onClick={handleToggleComplete}
            disabled={marking}
            className={`w-full py-5 rounded-2xl font-bold uppercase tracking-wider text-base transition-all flex items-center justify-center gap-2 ${
              isCompletedThisWeek
                ? 'bg-emerald-500/15 border-2 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20'
                : 'bg-pink-600 hover:bg-pink-500 text-white shadow-lg shadow-pink-500/20'
            }`}
            data-testid="toggle-day-complete-btn"
          >
            {marking ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Actualizando...</>
            ) : isCompletedThisWeek ? (
              <><CheckCircle2 className="w-5 h-5" /> Día completado · toca para desmarcar</>
            ) : (
              <><Check className="w-5 h-5" /> Marcar día como completado</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkoutDayDetail;
